import { Patient, PendingPatient } from "@domain/patient/patient";
import styled from "@emotion/styled";
import { TertiaryButton } from "@ui/components/buttons/tertiary-button";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { MenuItemProps } from "@ui/components/common/menu-button";
import { PatientDetailsContent } from "@ui/pages/patient-details/components/patient-details/patient-details-column";
import { row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import { DialogProps } from "primereact/dialog";
import React, { useContext, useState } from "react";

export interface ProgramBuilderActionsDialogProps extends DialogProps {
	programName: string;
	statusMessage: string;
	patient?: Patient | PendingPatient;
	programActions: MenuItemProps[];
	onNameChanged: (newName: string) => void;
}

export const ProgramBuilderActionsDialog = ({
	programName,
	patient,
	statusMessage,
	programActions,
	onNameChanged,
	...otherProps
}: ProgramBuilderActionsDialogProps): JSX.Element => {
	const dialogContext = useContext(DialogContext);
	const [name, setName] = useState(programName);
	return (
		<BaseDialog visible={true} width={DIALOG_SIZE.S} {...otherProps}>
			<DialogContainer>
				<Row>
					<span className="p-size-l-bold">{name}</span>
					<EditIconWrapper
						onClick={() => {
							dialogContext?.openModal(ModalId.RenameProgram, {
								programName: programName,
								renameProgram: (newProgramName: string) => {
									setName(newProgramName);
									onNameChanged(newProgramName);
								},
							});
						}}
					>
						{<i className={"pi pi-pencil black"} />}
					</EditIconWrapper>
				</Row>
				<span className="p-size-s">{statusMessage}</span>
				{isNotNil(patient) && (
					<PatientDetailsContainer>
						<PatientDetailsContent patient={patient} isLightMode isEditable={false} />
					</PatientDetailsContainer>
				)}
				{programActions.length > 0 && (
					<Options>
						{programActions.map(({ itemClassName, disabled, destructive, text, icon, onClick }) => (
							<TertiaryButton
								key={text}
								uiStyle={destructive ? "danger" : "primary"}
								disabled={disabled}
								onClick={onClick}
								icon={icon}
								className={itemClassName}
								label={text}
							/>
						))}
					</Options>
				)}
			</DialogContainer>
		</BaseDialog>
	);
};

const DialogContainer = styled.div`
	${stack(undefined, "flexstart", "stretch")};
	height: 100%;
`;
const Row = styled.div`
	${row(8, "flexstart", "center")};
`;
const PatientDetailsContainer = styled.div`
	margin: 24px 0;
	border-radius: 16px;
	padding: 16px;
	border: 1px solid ${colors.primary25};
`;

const EditIconWrapper = styled.div`
	height: 24px;
	width: 24px;
	${row(undefined, "center", "center")}
	cursor: pointer;

	i {
		color: ${colors.black};
	}
`;

const Options = styled.div`
	${stack(24, "center", "flexstart")};
`;
